import './styles/LocalisationWrapper.scss';

import {
  Chat,
  CheckCircle,
  DotsThreeCircle,
  Pencil,
  PlusCircle,
  Translate,
} from '@phosphor-icons/react';
import CombineIcons from '@whistler-ui/k2/components/CombineIcons/CombineIcons';
import { Modal } from '@whistler-ui/k2/components/Modal';
import { Stack } from '@whistler-ui/k2/components/Stack';
import { Tabs } from '@whistler-ui/k2/components/Tabs';
import { hasInvalidValues } from '@whistler-ui/k2/helpers';
import { useClassHelper } from '@whistler-ui/k2/hooks';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  EMERGENCY_ADDRESS_AUTOCOMPLETE_BRIDGEKEEPER_KEY,
  FAMILY_PLAN_BRIDGEKEEPER_KEY,
  LANGUAGE_KEYS,
} from '../../constants';
import { useBridgeKeeper } from '../../hooks';
import { useThemeStore } from '../../store/theme';
import {
  BaseLocalesFormPropsType,
  PrefixGroupConfig,
  TemplateDataType,
} from '../../types';
import { findVariantGroups } from './helpers/prefixGroups';
import { LanguageSelector } from './LanguageForm';
import {
  LocalisationWrapperOptions,
  LocalisationWrapperPropsType,
} from './types';

const componentName = 'c-LocalisationWrapper';

// Localisation groups that are only configurable when a matching
// configuration_settings flag is enabled. Fields belonging to a gated group are
// stripped from the effective template (and therefore from rendering, formik
// init, validation, and the save payload) when its flag is falsy.
const CONFIG_GATED_GROUPS: Record<string, string> = {
  family_plan_selection: 'supports_family_plan',
  family_plan_terms: 'supports_family_plan',
  // Cross-plan notices only occur when a Family Plan (linked) can coexist with a
  // Paired plan — irrelevant when the brand is paired-only.
  notifications: 'supports_family_plan',
  // Linked (sa-watch / standalone) watch-transfer copy variants.
  family_plan_transfer_watch_plan: 'supports_family_plan',
  family_plan_transfer_user_feedback: 'supports_family_plan',
  family_plan_transfer_terms_and_conditions: 'supports_family_plan',
  // Google Places copy (find-address, street number, address help, suggested
  // address) is only reachable when autocomplete is enabled for the brand.
  vowifi_address_autocomplete: 'emergency_address_autocomplete_enabled',
  vowifi_address_validation: 'emergency_address_autocomplete_enabled',
};

export const filterTemplateByConfig = (
  template: TemplateDataType,
  configurationSettings: Record<string, unknown> | undefined,
  prefixGroups: PrefixGroupConfig[] = [],
): TemplateDataType => {
  const staticHiddenGroups = Object.entries(CONFIG_GATED_GROUPS)
    .filter(([, flag]) => !configurationSettings?.[flag])
    .map(([group]) => group);
  const gatedPrefixes = prefixGroups.filter(
    ({ configFlag }) => !configurationSettings?.[configFlag],
  );

  if (staticHiddenGroups.length === 0 && gatedPrefixes.length === 0) {
    return template;
  }

  return Object.keys(template).reduce((acc, sectionKey) => {
    const section = template[sectionKey];
    const hiddenGroups = [
      ...staticHiddenGroups,
      ...gatedPrefixes.flatMap(({ prefix }) =>
        findVariantGroups(section, prefix),
      ),
    ];
    acc[sectionKey] = Object.keys(section).reduce((fields, fieldKey) => {
      const group = section[fieldKey].group;
      if (!group || !hiddenGroups.includes(group)) {
        fields[fieldKey] = section[fieldKey];
      }
      return fields;
    }, {} as TemplateDataType[string]);
    return acc;
  }, {} as TemplateDataType);
};

const LocalisationWrapper = (props: LocalisationWrapperPropsType) => {
  const [languageModalOpen, setLanguageModalOpen] = React.useState(false);
  const {
    form: Form,
    data,
    type,
    template: rawTemplate,
    prefixGroups,
    isLocaleComplete,
    ...rest
  } = props;

  // Family Plan localisation groups require both the brand config flag and the
  // `family_plan` BridgeKeeper feature flag. When the feature is off, force
  // supports_family_plan falsy so those groups are stripped from the template.
  const {
    showFeature: familyPlanFeatureEnabled,
    isLoading: familyPlanFlagLoading,
  } = useBridgeKeeper(FAMILY_PLAN_BRIDGEKEEPER_KEY);

  // Same gating for the VoWiFi Google Places copy groups: both the brand
  // config flag and the BridgeKeeper feature must be on.
  const { showFeature: autocompleteFeatureEnabled } = useBridgeKeeper(
    EMERGENCY_ADDRESS_AUTOCOMPLETE_BRIDGEKEEPER_KEY,
  );

  // The toggle skeleton only applies to tabs whose RAW template declares
  // variant groups for a configured prefix — while the flag resolves, the
  // filtered template can't tell "no pairs" apart from "pairs pending".
  const hasPrefixedGroups = Boolean(
    prefixGroups?.some(
      config =>
        findVariantGroups(rawTemplate[type] ?? {}, config.prefix).length > 0,
    ),
  );
  const configurationSettings = data?.useCaseSettings?.configuration_settings;
  const effectiveConfig = {
    ...configurationSettings,
    supports_family_plan:
      Boolean(configurationSettings?.supports_family_plan) &&
      familyPlanFeatureEnabled,
    emergency_address_autocomplete_enabled:
      Boolean(configurationSettings?.emergency_address_autocomplete_enabled) &&
      autocompleteFeatureEnabled,
  };
  const template = filterTemplateByConfig(
    rawTemplate,
    effectiveConfig,
    prefixGroups,
  );
  const locales = data?.localisationSettings || [];
  const hasLocales = locales.length > 0;
  const emptyLocales = !hasLocales;
  const { t } = useTranslation(['components']);
  const [activeTab, setActiveTab] = React.useState('0');
  const c = useClassHelper(componentName);
  const { colors } = useThemeStore();

  const isLocaleTabComplete = (
    localisationSetting: Record<string, any>,
    type: string,
    template: Record<string, unknown>,
  ) => {
    if (isLocaleComplete) {
      return isLocaleComplete(localisationSetting.locale, data);
    }

    const selectedSettings = localisationSetting.data?.[type];
    const selectedTemplate = template[type];

    return !hasInvalidValues(selectedSettings, selectedTemplate);
  };

  const getTabIcon = (
    localisationSetting: Record<string, any>,
    type: string,
    template: Record<string, unknown>,
  ) => {
    if (isLocaleTabComplete(localisationSetting, type, template)) {
      return <CheckCircle size={16} color={colors.success} />;
    }

    return <Pencil size={16} color={colors.fontcolorBrandPrimary} />;
  };

  const handleTabChange = useCallback(newActiveTab => {
    setActiveTab(prevActiveTab => {
      if (prevActiveTab === newActiveTab) {
        return prevActiveTab;
      }
      return newActiveTab;
    });
  }, []);

  return (
    <div className={componentName}>
      {emptyLocales ? (
        <div className={c('__empty')}>
          <b>{t('components:localisation_wrapper.empty.description')}</b>

          <div className={c('__details')}>
            <CombineIcons size={56}>
              <Chat size={56} color={colors.borderColor} />
              <Translate size={28} color={colors.borderColor} />
            </CombineIcons>
            <div>
              {t('components:localisation_wrapper.empty.no_languages_defined')}
            </div>
            <button
              className={c('__action')}
              onClick={() => setLanguageModalOpen(true)}
            >
              <Stack direction="row" className="gap-[0.2rem]!">
                <PlusCircle
                  size={16}
                  weight="fill"
                  color={colors.colorBrandPrimary}
                />
                <b>{t('components:localisation_wrapper.add_new_language')}</b>
              </Stack>
            </button>
          </div>
        </div>
      ) : (
        <section className={c('__content')}>
          <Tabs
            variant="unstyled"
            controlledActiveTab={activeTab}
            controlledOnChange={handleTabChange}
          >
            {locales.map((currentLocale: any, index: number) => {
              return (
                <Tabs.Item
                  key={index}
                  eventKey={`${index}`}
                  title={LANGUAGE_KEYS[currentLocale.locale]}
                  icon={getTabIcon(currentLocale, props.type, template)}
                  onlyRenderOnActive
                >
                  <Form
                    {...rest}
                    settings={data as BaseLocalesFormPropsType['settings']}
                    type={type}
                    defaultData={template}
                    prefixGroups={prefixGroups}
                    prefixGroupsLoading={
                      familyPlanFlagLoading && hasPrefixedGroups
                    }
                    locale={currentLocale.locale}
                    data={currentLocale.data}
                    canGoToPrevLocalisation={index > 0}
                    canGoToNextLocalisation={Boolean(locales[index + 1])}
                    goNextLocalisation={() => {
                      const isNextLocale = Boolean(locales[index + 1]);
                      if (isNextLocale) {
                        setActiveTab(prev => {
                          const parsedPrev = Number(prev);
                          const newTab = parsedPrev + 1;
                          return `${newTab}`;
                        });
                      }
                    }}
                    goPrevLocalisation={() => {
                      if (index > 0) {
                        setActiveTab(prev => {
                          const parsedPrev = Number(prev);
                          const newTab = parsedPrev - 1;
                          return `${newTab}`;
                        });
                      }
                    }}
                  />
                </Tabs.Item>
              );
            }, [])}
          </Tabs>
          <button
            className={c('__tab__action')}
            onClick={() => setLanguageModalOpen(true)}
          >
            <DotsThreeCircle size={24} color={colors.colorBrandPrimary} />
          </button>
        </section>
      )}

      <Modal
        show={languageModalOpen}
        onClose={() => setLanguageModalOpen(false)}
        showBtnClose={false}
        title=""
        actionButtonLabel={'Test'}
        actionButton={null}
      >
        <LanguageSelector
          brandId={props.brandId}
          tag={props.tag}
          environmentId={props.environmentId}
          closeModalCallback={() => setLanguageModalOpen(false)}
          template={template}
        />
      </Modal>
    </div>
  );
};
export const withLocalisation = (
  Component: React.FC<BaseLocalesFormPropsType>,
  type: string,
  template: TemplateDataType,
  prefixGroups?: PrefixGroupConfig[],
  options?: LocalisationWrapperOptions,
) => {
  return (props: BaseLocalesFormPropsType) => {
    return (
      <LocalisationWrapper
        {...props}
        form={Component}
        type={type}
        template={template}
        prefixGroups={prefixGroups}
        isLocaleComplete={options?.isLocaleComplete}
      />
    );
  };
};
